<template>
    <v-card flat color="transparent">
        <v-layout row wrap>
            <v-flex xs3 pa-0 ma-0>
                <router-link :to="$product.getProductRouteConfig(product)">
                    <v-img
                        :alt="imgAlt"
                        :lazy-src="lazyImgSrc"
                        :src="imgSrc"
                        :height="$vuetify.breakpoint.mdAndUp ? 200 : 100"
                        contain
                    />
                </router-link>
            </v-flex>

            <v-flex xs9 pa-0 ma-0 pl-2>
                <v-container pa-0 fill-height>
                    <v-layout row wrap pa-0 ma-0>
                        <v-flex xs12 pa-0 ma-0 align-self-start>
                            <v-layout row wrap pa-0 ma-0>
                                <v-flex xs12 md4 pa-0 ma-0>
                                    <router-link
                                        :to="$product.getProductRouteConfig(product)"
                                        class="title relate-product-title primary--text font-weight-light mb-1 text-decoration-none"
                                    >
                                        {{ title }}
                                    </router-link>

                                    <div class="caption">
                                        {{ shortDescription }}
                                    </div>

                                    <v-btn
                                        small
                                        flat
                                        class="subheading no-uppercase pa-0 ma-0"
                                        @click="extendShortDescription = !extendShortDescription"
                                        v-if="$vuetify.breakpoint.smAndDown"
                                    >
                                        <v-icon size="20" color="black" class="pl-0 pr-1">info</v-icon>
                                        {{ extendShortDescription ? 'less' : 'more' | translation }}
                                        <v-icon size="16" color="black">play_arrow</v-icon>
                                    </v-btn>
                                </v-flex>

                                <v-flex xs12 md8 pa-0 ma-0 :class="{ 'pl-2': $vuetify.breakpoint.mdAndUp }">
                                    <v-container pa-0 fill-height align-center>
                                        <v-layout row wrap mt-1>
                                            <v-flex shrink v-if="!hideAmountInput">
                                                <amount-input
                                                    :value.sync="productQuantity"
                                                    :product="product"
                                                    :current-merchant-id="merchantID"
                                                    :current-product-id="product.id"
                                                    hide-tier-price-info
                                                    class="pt-1"
                                                />
                                            </v-flex>

                                            <v-flex class="text-xs-right align-self-center">
                                                <div class="caption light--text text--darken-4">
                                                    <template v-if="originalPrice > price">
                                                        {{ 'instead' | translation }} {{ originalPrice | currency }}
                                                    </template>
                                                </div>

                                                <div class="title relate-product-title">
                                                    {{ 'Price' | translation }} {{ price | currency }}
                                                </div>
                                            </v-flex>
                                        </v-layout>
                                    </v-container>
                                </v-flex>
                            </v-layout>
                        </v-flex>

                        <v-flex xs12 pa-0 ma-0 align-self-end mb-2 v-if="$vuetify.breakpoint.mdAndUp">
                            <v-layout row wrap pa-0 ma-0 align-end>
                                <v-flex xs4 pa-0 ma-0>
                                    <v-btn
                                        small
                                        flat
                                        class="subheading no-uppercase pa-0 ma-0"
                                        @click="showDescription = !showDescription"
                                    >
                                        <v-icon size="20" color="black" class="pl-0 pr-1">info</v-icon>
                                        {{ showDescription ? 'less' : 'more' | translation }}
                                        <v-icon size="16" color="black">play_arrow</v-icon>
                                    </v-btn>
                                </v-flex>

                                <v-flex xs8 pa-0 ma-0 :class="{ 'pl-2': $vuetify.breakpoint.mdAndUp }">
                                    <basket-button
                                        v-if="price"
                                        :amount-value="productQuantity"
                                        :product="product"
                                        depressed
                                        :round="false"
                                        :current-product-id="product.id"
                                        :current-merchant-id="merchantID"
                                        :button-text="basketButtonText"
                                    />
                                </v-flex>
                            </v-layout>
                        </v-flex>
                    </v-layout>
                </v-container>
            </v-flex>

            <v-flex xs12 v-if="$vuetify.breakpoint.smAndDown" class="px-0">
                <basket-button
                    v-if="price"
                    :amount-value="productQuantity"
                    :product="product"
                    depressed
                    :round="false"
                    :current-product-id="product.id"
                    :current-merchant-id="merchantID"
                    :button-text="basketButtonText"
                    @beforeAddToBasket="$emit('beforeAddToBasket', $event)"
                    @afterAddToBasket="$emit('afterAddToBasket', $event)"
                    :handler="addToBasket"
                    ref="basketButton"
                />
            </v-flex>
        </v-layout>

        <v-layout row wrap v-if="showDescription">
            <v-flex xs12 class="caption">
                {{ description | striphtml }}
            </v-flex>
        </v-layout>

        <add-variant-to-basket-modal
            v-if="showVariant"
            v-model="showVariant"
            :product="product"
            :quantity="productQuantity"
            @beforeAddToBasket="$emit('beforeAddToBasket', $event)"
            @afterAddToBasket="$emit('afterAddToBasket', $event)"
            :hide-amount-input="hideAmountInput"
        />
    </v-card>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import AmountInput from './AmountInput';
import BasketButton from './BasketButton';

const parentModuleName = 'Product';
const { mapState, mapGetters } = createNamespacedHelpers(parentModuleName);

export default {
    name: 'RelatedProductsThumbnail',
    components: {
        AmountInput,
        BasketButton,
    },
    props: {
        product: {
            type: Object,
            default: {},
        },
        hideAmountInput: Boolean,
        initialQuantity: {},
        basketButtonText: {},
    },
    data() {
        return {
            productQuantity: this.initialQuantity || 1,
            showDescription: false,
            extendShortDescription: false,
            showVariant: false,
        };
    },
    computed: {
        merchantID() {
            let merchantID = '';
            const merchants = Object.values(this.product.merchant);
            for (const merchant of merchants) {
                merchantID = merchant.id;
                break;
            }
            return merchantID.toString();
        },
        imgAlt() {
            return this.product.images.default.image_description ? this.product.images.default.image_description : '';
        },
        imgSrc() {
            let src = '';
            if (this.product.images && this.product.images.default && this.product.images.default.medium) {
                src = this.product.images.default.medium;
            }
            return src;
        },
        lazyImgSrc() {
            let src = '';
            if (this.product.images && this.product.images.default && this.product.images.default.placeholder) {
                src = this.product.images.default.placeholder;
            }
            return src;
        },
        title() {
            return this.getProductAttributeByLanguage(this.product, 'product.name');
        },
        description() {
            return this.getProductAttributeByLanguage(this.product, 'product.description.long');
        },
        shortDescription() {
            const shortDescription = this.getProductAttributeByLanguage(this.product, 'product.description.long');
            const description = this.$options.filters.striphtml(shortDescription);

            if (this.extendShortDescription) {
                return description;
            } else {
                const shortDescriptionMaxLength = this.$vuetify.breakpoint.mdAndUp ? 50 : 100;
                const suffix = description.length > shortDescriptionMaxLength ? '...' : '';
                return `${description.substring(0, shortDescriptionMaxLength)}${suffix}`;
            }
        },
        price() {
            return this.product.price ? this.product.price.price : null;
        },
        originalPrice() {
            return this.product.price ? this.product.price.original_price_inkl_vat : null;
        },
        ratingValue() {
            return this.product.rating ? this.product.rating : null;
        },
        reviewCount() {
            return this.product.reviewCount ? this.product.reviewCount : null;
        },
        ...mapGetters(['getProductAttributeByLanguage']),
        ...mapState({
            currentProduct: 'product',
        }),
    },
    methods: {
        addToBasket() {
            if(this.product.variantsIds && this.product.variantsIds.length){
                this.showVariant = true;
            } else {
                this.$refs.basketButton.buyProduct();
            }
        },
    },
};
</script>

<style scoped>
.original-price {
    color: #929292;
}
.no-uppercase {
    text-transform: none;
}
.title.relate-product-title {
    font-size: 18px !important;
}
</style>
